import { useEffect, useState } from "react";

/**
 * Single source of truth for a time-boxed marketing campaign (a product launch, a
 * seasonal push, a planned-maintenance notice — anything that wants a temporary top
 * banner, a sidebar prompt, and a dialog).
 *
 * Deliberately concrete rather than a config-driven framework: to run a campaign, edit
 * the constants below and the copy in CampaignBanner/CampaignDialog directly, then flip
 * CAMPAIGN_ACTIVE. The layout plumbing (the --campaign-banner-height CSS variable read
 * by index.js, Spa.js and ResponsiveDrawer) is the part worth keeping between campaigns;
 * the copy is meant to be overwritten each time.
 */

// Master switch, and the only thing that has to change to put a campaign live: the
// default window below is open-ended, so true is enough on its own. Set back to false to
// stand the whole campaign down (banner, sidebar prompt, dialog).
export const CAMPAIGN_ACTIVE = false;

// Identifies the current campaign. Bump this whenever a new campaign starts — it keys the
// "dialog already auto-opened" flag below, so changing it re-arms the dialog for everyone.
export const CAMPAIGN_ID = "example-campaign-2026";

// The campaign window, as epoch milliseconds (Date.UTC keeps these unambiguous wherever
// they're edited from). Defaults to an open-ended window so CAMPAIGN_ACTIVE alone is
// enough to put a campaign live; narrow them for a timed one.
//
// Setting a start in the future puts the campaign in its "pre" phase: no banner, but the
// sidebar prompt counts down and the dialog runs its run-up copy.
export const CAMPAIGN_START_MS = Date.UTC(2020, 0, 1, 0, 0, 0);
export const CAMPAIGN_END_MS = Date.UTC(2100, 0, 1, 0, 0, 0);

// Normalises a timestamp to local midnight so day differences aren't skewed by
// time-of-day or DST — only whole calendar days matter for "in X days" copy.
const startOfLocalDay = (ms) => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/**
 * Returns { phase, daysUntil } for the given instant (defaults to now).
 * phase: "pre" (before the window) | "live" (inside it) | "over" (after, or switched off).
 * daysUntil: whole local-calendar days until the start, only meaningful for "pre".
 */
export const getCampaignPhase = (nowMs = Date.now()) => {
  if (!CAMPAIGN_ACTIVE) return { phase: "over", daysUntil: 0 };
  if (nowMs >= CAMPAIGN_END_MS) return { phase: "over", daysUntil: 0 };
  if (nowMs >= CAMPAIGN_START_MS) return { phase: "live", daysUntil: 0 };

  const daysUntil = Math.round(
    (startOfLocalDay(CAMPAIGN_START_MS) - startOfLocalDay(nowMs)) / 86_400_000,
  );
  return { phase: "pre", daysUntil };
};

/**
 * The campaign start rendered in the viewer's own timezone, e.g. "8am BST",
 * "5pm GMT+10", "12:30pm GMT+5:30". Lets pre-campaign copy say exactly when the banner
 * will appear, so anyone opening the app early understands why it isn't there yet.
 *
 * hourCycle "h12" rather than hour12: true — the latter resolves to an h11 cycle in
 * en-GB, which renders midnight as "0am" and half twelve as "0:30pm". Minutes are
 * included only when the offset needs them (India, Nepal, and similar).
 */
export const getLocalStartTime = () => {
  const date = new Date(CAMPAIGN_START_MS);
  const minutes = date.getMinutes();

  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    ...(minutes !== 0 && { minute: "2-digit" }),
    hourCycle: "h12",
    timeZoneName: "short",
  })
    .format(date)
    .replace(/\s?(am|pm)/i, (match) => match.trim().toLowerCase());
};

// Records that the campaign dialog has already auto-opened in this browser, so a reload
// or a second visit during the campaign doesn't pop it again. The stored value is the
// campaign id, so a new CAMPAIGN_ID invalidates the old flag automatically — there is
// only ever one key, and nothing to clean up between campaigns.
//
// Deliberately localStorage rather than the user's ui_state: this is throwaway,
// time-boxed state that also needs to work for signed-out visitors, and keeping it on the
// client means running a campaign is a pure frontend change with no backend deploy.
// Durable per-account preferences (the install/import sidebar prompts) still use ui_state.
const DIALOG_SEEN_KEY = "curate-campaign-dialog-seen";

export const hasSeenCampaignDialog = () =>
  localStorage.getItem(DIALOG_SEEN_KEY) === CAMPAIGN_ID;

export const markCampaignDialogSeen = () =>
  localStorage.setItem(DIALOG_SEEN_KEY, CAMPAIGN_ID);

// Delay before the dialog auto-opens, so it lands after the page has painted and settled
// rather than fighting the initial render.
export const AUTO_OPEN_DELAY_MS = 2000;

// Keeps the phase live in a component: recomputed on mount, every 60s, and whenever the
// tab regains visibility (covers a tab left open across the campaign boundary). Does no
// work at all once the phase is "over", which includes the whole time CAMPAIGN_ACTIVE is
// false — no timer runs between campaigns.
export const useCampaignPhase = () => {
  const [state, setState] = useState(() => getCampaignPhase());

  useEffect(() => {
    if (state.phase === "over") return;

    const recompute = () => setState(getCampaignPhase());
    const interval = setInterval(recompute, 60_000);
    document.addEventListener("visibilitychange", recompute);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", recompute);
    };
  }, [state.phase]);

  return state;
};

// Sidebar prompt copy. Split into a fixed lead-in and the rest so the drawer can colour
// just the lead-in. Overwrite both for each campaign.
export const PROMPT_LEAD_IN = "Something's coming!";

export const getPromptMessage = (phase, daysUntil) => {
  if (phase === "live") return "Find out more";
  if (daysUntil === 0) return "Later today";
  if (daysUntil === 1) return "Tomorrow";
  return `In ${daysUntil} days`;
};
